import { SupabaseClient } from "@supabase/supabase-js";
import { supabase as defaultSupabase } from "@/lib/supabase";
import { MapData } from "@/features/maps/core/types/MapData";
import { formatForDisplay, formatForStorage } from "@/utils/dates";

const MAPS_TABLE = "maps";

const convertToDisplay = <T extends Partial<MapData> | null | undefined>(map: T): T => {
  if (!map) return map;
  const cloned = { ...map };
  if (cloned.birthDate) {
    cloned.birthDate = formatForDisplay(cloned.birthDate);
  }
  return cloned;
};

const convertToStorage = <T extends Partial<MapData> | null | undefined>(map: T): T => {
  if (!map) return map;
  const cloned = { ...map };
  if (cloned.birthDate) {
    const stored = formatForStorage(cloned.birthDate);
    if (stored) {
      cloned.birthDate = stored;
    }
  }
  return cloned;
};

const convertArrayToDisplay = <T extends Partial<MapData>>(maps: T[]): T[] => maps.map(convertToDisplay);

/**
 * Raw DB: Create a new map
 */
export async function dbCreateMap(
  data: Partial<MapData>,
  client: SupabaseClient = defaultSupabase,
): Promise<MapData> {
  const storageData = convertToStorage(data);
  const { _completeness_flags, ...rawDbData } = storageData as Partial<MapData> & { _completeness_flags?: unknown };
  const { data: newMap, error } = await client
    .from(MAPS_TABLE)
    .insert(rawDbData)
    .select()
    .single();

  if (error) throw error;
  return convertToDisplay(newMap) as MapData;
}

/**
 * Raw DB: Update an existing map
 */
export async function dbUpdateMap(
  id: string,
  updates: Partial<MapData>,
  client: SupabaseClient = defaultSupabase,
): Promise<void> {
  const storageUpdates = convertToStorage(updates);
  const { _completeness_flags, ...rawDbUpdates } = storageUpdates as Partial<MapData> & { _completeness_flags?: unknown };
  const { error } = await client
    .from(MAPS_TABLE)
    .update({
      ...rawDbUpdates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) throw error;
}

/**
 * Raw DB: Soft Delete a map (marks deleted_at)
 */
export async function dbDeleteMap(
  id: string,
  client: SupabaseClient = defaultSupabase,
): Promise<void> {
  const { error } = await client
    .from(MAPS_TABLE)
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);

  if (error) throw error;
}

/**
 * Raw DB: Hard Delete a map (permanently removes row from DB, reserved for GDPR/Privacy account erasure)
 */
export async function dbHardDeleteMap(
  id: string,
  client: SupabaseClient = defaultSupabase,
): Promise<void> {
  const { error } = await client.from(MAPS_TABLE).delete().eq("id", id);

  if (error) throw error;
}

/**
 * Raw DB: Fetch maps with pagination
 */
export async function dbFetchMaps(
  userId: string,
  from: number,
  to: number,
  client: SupabaseClient = defaultSupabase,
): Promise<{ data: MapData[]; count: number | null }> {
  const { data, error, count } = await client
    .from(MAPS_TABLE)
    .select("*", { count: "exact" })
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .range(from, to);

  if (error) throw error;
  return { data: convertArrayToDisplay(data || []), count };
}

/**
 * Raw DB: Fetch all maps for a user
 */
export async function dbFetchAllMaps(
  userId: string,
  client: SupabaseClient = defaultSupabase,
): Promise<MapData[]> {
  const { data, error } = await client
    .from(MAPS_TABLE)
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return convertArrayToDisplay((data as MapData[]) || []);
}

/**
 * Raw DB: Check for existing map with same identifiers
 */
export async function dbFindDuplicateMap(
  params: {
    userId: string;
    firstName: string;
    familyName: string | null;
    motherName: string | null;
    birthDate: string | null;
  },
  client: SupabaseClient = defaultSupabase,
): Promise<string | null> {
  const { userId, firstName, familyName, motherName, birthDate } = params;
  const storageBirthDate = birthDate ? (formatForStorage(birthDate) || birthDate) : null;

  let query = client
    .from(MAPS_TABLE)
    .select("id")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .eq("firstName", firstName);

  if (familyName) {
    query = query.eq("familyName", familyName);
  } else {
    query = query.is("familyName", null);
  }

  if (motherName) {
    query = query.eq("motherName", motherName);
  } else {
    query = query.is("motherName", null);
  }

  if (storageBirthDate) {
    query = query.eq("birthDate", storageBirthDate);
  } else {
    query = query.is("birthDate", null);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    throw error;
  }

  return data?.id || null;
}

/**
 * Raw DB: Batch insert maps
 */
export async function dbBatchInsertMaps(
  payload: Partial<MapData>[],
  client: SupabaseClient = defaultSupabase,
): Promise<void> {
  const storagePayload = payload.map(item => {
    const { _completeness_flags, ...rest } = convertToStorage(item) as Partial<MapData> & { _completeness_flags?: unknown };
    return rest;
  });
  const { error } = await client.from(MAPS_TABLE).insert(storagePayload);

  if (error) throw error;
}

/**
 * Raw DB: Fetch minimal map data for matching/merging
 */
export async function dbFetchUserMapsForMatching(
  userId: string,
  client: SupabaseClient = defaultSupabase,
): Promise<Partial<MapData>[]> {
  const { data, error } = await client
    .from(MAPS_TABLE)
    .select("id, firstName, familyName, motherName, birthDate, labels, notes")
    .eq("user_id", userId)
    .is("deleted_at", null);

  if (error) throw error;
  return convertArrayToDisplay((data as Partial<MapData>[]) || []);
}

/**
 * Raw DB: Fetch maps by specific label for user
 */
export async function dbFetchMapsByLabel(
  userId: string,
  label: string,
  client: SupabaseClient = defaultSupabase,
): Promise<MapData[]> {
  const { data, error } = await client
    .from(MAPS_TABLE)
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .contains("labels", [label]);

  if (error) throw error;
  return convertArrayToDisplay((data as MapData[]) || []);
}

import { sanitizeSearchTerm } from "@/utils/security";

/**
 * Raw DB: Search user maps by name
 * Supports searching first/family names with multiple terms
 *
 * Optimized with 'pg_trgm' (GIN Index) for efficient fuzzy matching.
 */
export async function dbSearchUserMaps(
  userId: string,
  searchTerms: string[],
  client: SupabaseClient = defaultSupabase,
): Promise<MapData[]> {
  if (searchTerms.length === 0) return [];

  // Limit search terms and filter out short ones
  const validTerms = searchTerms
    .slice(0, 5)
    .filter((t) => t.length >= 2)
    .map(sanitizeSearchTerm); // Sanitize each term

  if (validTerms.length === 0) return [];

  let query = client
    .from(MAPS_TABLE)
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .limit(20);

  const conditions = validTerms
    .map((part) => {
      // Use % for full fuzzy search with pg_trgm
      return `firstName.ilike.%${part}%, familyName.ilike.%${part}%`;
    })
    .join(",");

  query = query.or(conditions);

  const { data, error } = await query;

  if (error) throw error;
  return convertArrayToDisplay((data as MapData[]) || []);
}

/**
 * Raw DB: Fetch specific map metadata for merging
 */
export async function dbFetchMapMetadata(
  id: string,
  client: SupabaseClient = defaultSupabase,
): Promise<Partial<MapData> | null> {
  const { data, error } = await client
    .from(MAPS_TABLE)
    .select("labels, notes, birth_time, birth_location, birth_lat, birth_lon, timezone, gender, soul_root")
    .eq("id", id)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) throw error;
  return data as Partial<MapData> | null;
}

/**
 * Raw DB: Fetch full map by ID
 */
export async function dbFetchMapById(
  id: string,
  client: SupabaseClient = defaultSupabase,
): Promise<MapData | null> {
  const { data, error } = await client
    .from(MAPS_TABLE)
    .select("*")
    .eq("id", id)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return convertToDisplay(data) as MapData;
}

/**
 * Raw DB: Fetch maps with identical 4 core calculations for a user (Same Map Peers)
 */
export async function dbFetchSameMapPeers(
  userId: string,
  innerSign: number,
  outerSign: number,
  turValue: string,
  profileNumber: number,
  client: SupabaseClient = defaultSupabase,
): Promise<MapData[]> {
  const { data, error } = await client
    .from(MAPS_TABLE)
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .eq("inner_sign_number", innerSign)
    .eq("surrounding_sign_number", outerSign)
    .eq("tur_value", turValue)
    .eq("profile_number", profileNumber)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return convertArrayToDisplay((data as MapData[]) || []);
}


